import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import AboutBigButton from "@/components/AboutBigButton";
import PhoneInput from "@/components/PhoneInput";
import MultiPhoneInput, { serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import ClientsSection from "@/components/ClientsSection";
import LanguageToggle from "@/components/LanguageToggle";
import { ArrowLeft, UploadSimple } from "@phosphor-icons/react";

export default function Register() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", contact_person_name: "", email: "", password: "", confirm: "",
    commercial_register_number: "", address: "", google_maps_url: "",
    cr_image_url: "",
  });
  const [phones, setPhones] = useState([{ number: "+966", is_primary: true }]);
  const [clients, setClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (user) return <Navigate to="/" replace />;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("حجم الصورة أقل من 2MB");
    const reader = new FileReader();
    reader.onload = () => set("cr_image_url", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("كلمتا المرور غير متطابقتين");
    if (form.password.length < 6) return toast.error("كلمة المرور 6 أحرف على الأقل");
    if (!form.contact_person_name || form.contact_person_name.trim().length < 3) {
      return toast.error("يرجى إدخال اسم مسؤول التواصل (3 أحرف على الأقل)");
    }
    const phoneError = validatePhonesList(phones);
    if (phoneError) return toast.error(phoneError);
    // Validate clients (if any) — name + phone1 mandatory for each card user added.
    const invalidClient = clients.find((c) => !((c.name || "").trim()) || !((c.phone1 || "").trim()));
    if (invalidClient) {
      return toast.error("كل عميل مضاف يحتاج اسم ورقم جوال");
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      delete payload.confirm;
      const cleanedPhones = serializePhones(phones);
      payload.phones = cleanedPhones;
      payload.phone = cleanedPhones[0]?.number || "";
      payload.clients = clients.map((c) => ({
        name: (c.name || "").trim(),
        email: (c.email || "").trim() || undefined,
        phone1: (c.phone1 || "").trim(),
        phone2: (c.phone2 || "").trim() || undefined,
      }));
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("mz_token", data.token);
      localStorage.setItem("mz_user", JSON.stringify(data.user));
      toast.success("تم إنشاء الحساب بنجاح");
      window.location.href = "/";
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground py-8 px-4" dir="rtl"
      style={{ background: "radial-gradient(ellipse at top left, rgba(201,169,97,0.10) 0%, transparent 45%), #050a15" }}>
      <div className="max-w-4xl mx-auto relative">
        <Link to="/login" className="inline-flex items-center gap-2 text-primary hover:text-[#E1C484] mb-6" data-testid="back-to-login">
          <ArrowLeft size={18} /> عودة لتسجيل الدخول
        </Link>
        <div className="text-center mb-8">
          <div className="inline-block mb-2">
            <Logo variant="mark" size={72} />
          </div>
          {/* Language toggle (spec 2026-07-24 v9 §I18n) — parked in the
              header so a user who accidentally landed here in English can
              instantly flip back. The Register form itself stays Arabic
              (Saudi-specific fields like CR number make English pointless). */}
          <div className="absolute top-6 left-6">
            <LanguageToggle />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-4">تسجيل شركة جديدة</h1>
          <p className="text-white/50 mt-2">أنشئ حساباً لشركتك وابدأ إدارة حجوزاتك مع مراسم الزوار</p>
        </div>

        <form onSubmit={submit} className="bg-card border border-primary/15 rounded-2xl p-6 md:p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">اسم الشركة *</div>
              <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mz-input" data-testid="reg-name" placeholder="مثال: مؤسسة الرحمة للنقل" />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">اسم مسؤول التواصل *</div>
              <input
                required
                minLength={3}
                value={form.contact_person_name}
                onChange={(e) => set("contact_person_name", e.target.value)}
                className="mz-input"
                data-testid="reg-contact-person"
                placeholder="الشخص المسؤول عن الحجوزات"
              />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">البريد الإلكتروني *</div>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mz-input text-right" data-testid="reg-email" placeholder="example@company.com" />
            </label>
            <div>
              <div className="text-xs text-white/60 mb-2 text-right">أرقام التواصل</div>
              <MultiPhoneInput
                value={phones}
                onChange={setPhones}
                testIdPrefix="reg-phones"
                maxEntries={5}
              />
            </div>
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">كلمة المرور *</div>
              <input required type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mz-input text-right" data-testid="reg-password" placeholder="••••••••" />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">تأكيد كلمة المرور *</div>
              <input required type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} className="mz-input text-right" data-testid="reg-confirm" placeholder="••••••••" />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">رقم السجل التجاري</div>
              <input value={form.commercial_register_number} onChange={(e) => set("commercial_register_number", e.target.value)} className="mz-input text-right" data-testid="reg-cr" dir="ltr" />
            </label>
            <label className="block md:col-span-2">
              <div className="text-xs text-white/60 mb-2 text-right">العنوان</div>
              <input value={form.address} onChange={(e) => set("address", e.target.value)} className="mz-input" data-testid="reg-address" placeholder="المدينة، الحي، الشارع" />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-2 text-right">رابط Google Maps</div>
              <input value={form.google_maps_url} onChange={(e) => set("google_maps_url", e.target.value)} className="mz-input text-right" data-testid="reg-maps" dir="ltr" placeholder="https://maps.app.goo.gl/…" />
            </label>
            <label className="block md:col-span-3">
              <div className="text-xs text-white/60 mb-2 text-right">صورة السجل التجاري (اختياري)</div>
              <div className="flex items-center gap-3">
                <label className="flex-1 flex items-center gap-3 mz-input cursor-pointer hover:border-primary/60">
                  <UploadSimple size={20} className="text-primary" />
                  <span className="text-white/60 text-sm truncate">
                    {form.cr_image_url ? "تم اختيار الصورة ✓" : "اختر صورة (JPG/PNG - أقصى 2MB)"}
                  </span>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="reg-cr-image" />
                </label>
                {form.cr_image_url && (
                  <img src={form.cr_image_url} alt="preview" className="w-14 h-14 rounded-lg object-cover border border-primary/30" />
                )}
              </div>
            </label>
          </div>

          {/* Company Clients (optional) */}
          <ClientsSection
            value={clients}
            onChange={setClients}
            testId="reg-clients"
          />

          <button
            type="submit"
            disabled={submitting}
            data-testid="reg-submit"
            className="w-full py-4 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold text-lg transition-all disabled:opacity-60"
          >
            {submitting ? "جارٍ الإنشاء…" : "إنشاء الحساب"}
          </button>

          <p className="text-center text-white/50 text-sm">
            لديك حساب مسبقاً؟{" "}
            <Link to="/login" className="text-primary hover:text-[#E1C484]" data-testid="reg-to-login">
              سجّل الدخول
            </Link>
          </p>
          <AboutBigButton />
        </form>
      </div>
    </div>
  );
}
